import apiClient from '../core';
import type {
  Site,
  CreateSiteRequest,
  PublishSiteRequest,
  Page,
  CreateSiteTemplateRequest,
  CreateSiteFromTemplateRequest,
  CreateSiteFromTemplateResponse,
} from '../../schemas/validation';

// ─── Publishing Workflow Types ────────────────────────────────────────

export interface UnpublishedSummaryItem {
  id: string;
  title?: string;
  name?: string;
  slug?: string;
  status: 'never_published' | 'changed' | 'up_to_date';
}

export interface UnpublishedSummary {
  hasChanges: boolean;
  summary: {
    pages: { total: number; changed: number; items: UnpublishedSummaryItem[] };
    layouts: { total: number; changed: number; items: UnpublishedSummaryItem[] };
    blogPosts: { total: number; changed: number; items: UnpublishedSummaryItem[] };
  };
}

export interface PublishAllFailedItem {
  id: string;
  title: string;
  error: string;
}

export interface PublishAllResult {
  message: string;
  results: {
    layouts: { published: number; skipped: number; failed?: PublishAllFailedItem[] };
    blogPosts: { published: number; skipped: number; failed?: PublishAllFailedItem[] };
    pages: {
      published: number;
      skipped: number;
      republishedByLayout: number;
      failed?: PublishAllFailedItem[];
    };
  };
}

export const sitesApi = {
  async list(): Promise<Site[]> {
    const response = await apiClient.get<Site[]>('/sites');

    console.log('🔧 API: Sites list response:', {
      count: response.data?.length,
      sites: response.data?.map(s => ({
        id: s.id,
        name: s.name,
        theme: s.theme,
      })),
    });

    // Store sites in localStorage for preview fallback
    if (response.data) {
      try {
        localStorage.setItem('sites', JSON.stringify(response.data));
      } catch {
        // Quota exceeded — non-fatal
      }
    }
    return response.data;
  },

  async create(data: CreateSiteRequest & { theme?: string }): Promise<Site> {
    const response = await apiClient.post<Site>('/sites', data);
    return response.data;
  },

  async update(
    siteId: string,
    data: Partial<
      CreateSiteRequest & {
        theme?: string;
        customDomain?: string | null;
        faviconUrl?: string | null;
        brandPackageId?: string | null;
        // SEO settings
        seoTitle?: string | null;
        seoDescription?: string | null;
        seoKeywords?: string | null;
        ogTitle?: string | null;
        ogDescription?: string | null;
        ogImage?: string | null;
        // Analytics and tracking
        googleAnalyticsId?: string | null;
        googleTagManagerId?: string | null;
        // Meta tracking
        metaPixelId?: string | null;
        metaAccessToken?: string | null; // Write-only, not returned
        metaTestEventCode?: string | null;
        // Custom scripts
        customHeadScripts?: string | null;
        customBodyScripts?: string | null;
      }
    >
  ): Promise<Site> {
    console.log('🔧 API: Updating site', siteId, 'with data:', data);

    const response = await apiClient.put<Site>(`/sites/${siteId}`, data);

    console.log('🔧 API: Update response:', {
      status: response.status,
      data: response.data,
      theme: response.data?.theme,
    });

    // Update site in localStorage for preview fallback
    if (response.data) {
      try {
        const sitesData = localStorage.getItem('sites');
        if (sitesData) {
          const sites = JSON.parse(sitesData);
          const index = sites.findIndex((s: Site) => s.id === siteId);
          if (index !== -1) {
            sites[index] = response.data;
            localStorage.setItem('sites', JSON.stringify(sites));
          }
        }
      } catch {
        // Quota exceeded or parse error — non-fatal
      }
    }

    return response.data;
  },

  async delete(siteId: string): Promise<void> {
    await apiClient.delete(`/sites/${siteId}`);
  },

  async publish(siteId: string, data: PublishSiteRequest): Promise<Site> {
    const response = await apiClient.put<Site>(`/sites/${siteId}`, data);
    return response.data;
  },

  async getPages(siteId: string): Promise<Page[]> {
    const response = await apiClient.get<Page[]>(`/sites/${siteId}/pages`);
    return response.data;
  },

  // Site Template methods
  async saveAsTemplate(
    siteId: string,
    data: CreateSiteTemplateRequest
  ): Promise<{
    message: string;
    template: {
      id: string;
      name: string;
      description?: string;
      tags?: string;
      isPublic: boolean;
      isSystem?: boolean;
      usageCount: number;
      createdAt: string;
    };
  }> {
    const response = await apiClient.post<{
      message: string;
      template: {
        id: string;
        name: string;
        description?: string;
        tags?: string;
        isPublic: boolean;
        isSystem?: boolean;
        usageCount: number;
        createdAt: string;
      };
    }>(`/sites/${siteId}/save-template`, data);
    return response.data;
  },

  async createFromTemplate(
    data: CreateSiteFromTemplateRequest
  ): Promise<CreateSiteFromTemplateResponse> {
    const response = await apiClient.post<CreateSiteFromTemplateResponse>(
      '/sites/from-template',
      data
    );
    return response.data;
  },

  // ─── Publishing Workflow ─────────────────────────────────────────

  /** Get summary of unpublished changes across pages, layouts, and blog posts */
  async getUnpublishedSummary(siteId: string): Promise<UnpublishedSummary> {
    const response = await apiClient.get<UnpublishedSummary>(
      `/sites/${siteId}/unpublished-summary`
    );
    return response.data;
  },

  /** Publish all changed layouts, blog posts, and pages in one shot */
  async publishAll(siteId: string): Promise<PublishAllResult> {
    const response = await apiClient.post<PublishAllResult>(`/sites/${siteId}/publish-all`);
    return response.data;
  },

  /** Set (or clear) the home page for a site */
  async setHomePage(
    siteId: string,
    pageId: string | null
  ): Promise<{
    message: string;
    homePageId: string | null;
    resolvedHomePage: { id: string; title: string; slug: string } | null;
  }> {
    const response = await apiClient.put<{
      message: string;
      homePageId: string | null;
      resolvedHomePage: { id: string; title: string; slug: string } | null;
    }>(`/sites/${siteId}/home-page`, { pageId });
    return response.data;
  },

  /** Get the resolved home page for a site (includes fallback info) */
  async getHomePage(siteId: string): Promise<{
    homePageId: string | null;
    isExplicit: boolean;
    resolvedHomePage: { id: string; title: string; slug: string; isPublished: boolean } | null;
    totalPages: number;
  }> {
    const response = await apiClient.get<{
      homePageId: string | null;
      isExplicit: boolean;
      resolvedHomePage: { id: string; title: string; slug: string; isPublished: boolean } | null;
      totalPages: number;
    }>(`/sites/${siteId}/home-page`);
    return response.data;
  },

  /**
   * DNS records the user must add at their DNS provider for a custom domain.
   * Pulled live from Vercel so the values match Vercel's current routing
   * infrastructure (project-specific CNAMEs, new IP ranges, etc.).
   */
  async getDnsRecords(siteId: string): Promise<{
    domain: string;
    verified: boolean;
    usingFallback: boolean;
    records: Array<{
      type: 'A' | 'CNAME' | 'TXT' | string;
      name: string;
      value: string;
      description?: string;
    }>;
  }> {
    const response = await apiClient.get<{
      domain: string;
      verified: boolean;
      usingFallback: boolean;
      records: Array<{ type: string; name: string; value: string; description?: string }>;
    }>(`/custom-domains/${siteId}/dns-records`);
    return response.data;
  },

  // Custom domain verification
  async getStudioCaptureForms(siteId: string): Promise<{
    linked: boolean;
    studioAppOrigin?: string;
    forms: Array<{
      id: string;
      name: string;
      shareToken: string;
      desktopHeight: number;
      mobileHeight: number;
    }>;
  }> {
    const response = await apiClient.get(`/sites/${siteId}/studio-capture-forms`);
    return response.data;
  },

  async verifyDomain(siteId: string): Promise<{
    domain: string;
    dnsConfigured: boolean;
    aRecord: boolean;
    cnameRecord: boolean;
    sslActive: boolean;
    errors: string[];
  }> {
    const response = await apiClient.get<{
      domain: string;
      dnsConfigured: boolean;
      aRecord: boolean;
      cnameRecord: boolean;
      sslActive: boolean;
      errors: string[];
    }>(`/custom-domains/${siteId}/verify`);
    return response.data;
  },
};
